import { Button, Flex } from '@/components';
import { t } from '@/i18n';
import { Z_INDEX_TARGET_ENV_BANNER } from '@/utils';
import { CloseOutlined } from '@ant-design/icons';
import { type FC, useCallback, useState } from 'react';
import { getTargetEnvBannerColor, getTargetEnvLabel } from './target-env';

const BANNER_HEIGHT = 28;
const CLOSE_BUTTON_SIZE = 20;

/**
 * @description 検証環境であることを画面下端のバナーで警告する。
 * 表示対象は stg のみ（prod / preprod / dev / local / mock では表示しない）。
 * レイアウトを押し縮めると sticky なヘッダー・フッターの位置がずれるため、
 * position: fixed で浮かせて本番と同じ寸法を保つ。
 * ✕ で閉じられるが、閉じた状態は保持しないため再読込で再表示される。
 */
export const TargetEnvBanner: FC = () => {
  const [isVisible, setIsVisible] = useState<boolean>(true);

  const handleClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const bannerColor = getTargetEnvBannerColor();

  if (!bannerColor || !isVisible) {
    return null;
  }

  return (
    <Flex
      role="alert"
      align="center"
      justify="center"
      gap={8}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: BANNER_HEIGHT,
        // 右側は閉じるボタンと文言が重ならないように余白を広く取る
        padding: '0 40px 0 16px',
        background: bannerColor,
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.5,
        boxShadow: '0 -2px 10px rgba(16, 32, 27, 0.28)',
        zIndex: Z_INDEX_TARGET_ENV_BANNER,
      }}
    >
      <span style={{ whiteSpace: 'nowrap' }}>
        {t('target-env-banner-lead', { env: getTargetEnvLabel() })}
      </span>
      <span style={{ fontWeight: 500 }}>{t('target-env-banner-detail')}</span>
      <Button
        type="text"
        size="small"
        icon={<CloseOutlined />}
        aria-label={t('target-env-banner-close')}
        onClick={handleClose}
        style={{
          position: 'absolute',
          right: 10,
          top: (BANNER_HEIGHT - CLOSE_BUTTON_SIZE) / 2,
          width: CLOSE_BUTTON_SIZE,
          height: CLOSE_BUTTON_SIZE,
          minWidth: CLOSE_BUTTON_SIZE,
          fontSize: 12,
          color: '#fff',
          background: 'rgba(255, 255, 255, 0.18)',
        }}
      />
    </Flex>
  );
};
